import React from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import { useEffect } from 'react';
import './MapModal.css';

const defaultAvatar = '/blankprofile.png';

function createProfilePinIcon(imageUrl) {
  return L.divIcon({
    className: 'profile-pin-icon',
    html: `
      <svg width="36" height="46" viewBox="0 0 36 46" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M18 46C18 46 36 28.5 36 18C36 8.7452 27.2548 0 18 0C8.7452 0 0 8.7452 0 18C0 28.5 18 46 18 46Z" fill="#1976d2" stroke="#1976d2" stroke-width="2"/>
        <defs>
          <clipPath id="circleClip">
            <circle cx="18" cy="18" r="15" />
          </clipPath>
        </defs>
        <image href="${imageUrl}" x="3" y="3" width="30" height="30" clip-path="url(#circleClip)" preserveAspectRatio="xMidYMid slice"/>
        <circle cx="18" cy="18" r="15" fill="none" stroke="#fff" stroke-width="2.2"/>
      </svg>
    `,
    iconSize: [36, 46],
    iconAnchor: [18, 46],
    popupAnchor: [0, -46]
  });
}

function MapInvalidator() {
  const map = useMap();
  useEffect(() => {
    setTimeout(() => {
      map.invalidateSize();
    }, 200);
  }, [map]);
  return null;
}

function MapModal({ isOpen, onClose, dinners, onDinnerClick, searchLocation }) {
  const defaultPosition = [32.0853, 34.7818]; // תל אביב

  // פונקציה לחישוב המרכז של הארוחות
  const calculateMapCenter = () => {
    if (dinners.length === 0) {
      return defaultPosition;
    }

    // אם יש חיפוש לפי מיקום, ננסה למצוא את המיקום הזה
    if (searchLocation) {
      // נחפש ארוחה עם המיקום הזה
      const matchingDinner = dinners.find(dinner => {
        const address = dinner.address || {};
        const dinnerLocation = `${address.city}, ${address.country}`.toLowerCase();
        return dinnerLocation.includes(searchLocation.toLowerCase());
      });

      if (matchingDinner) {
        let lat = matchingDinner.address?.lat;
        let lng = matchingDinner.address?.lng;
        if ((!lat || !lng) && Array.isArray(matchingDinner.address?.coords) && matchingDinner.address.coords.length === 2) {
          lat = matchingDinner.address.coords[0];
          lng = matchingDinner.address.coords[1];
        }
        if (lat && lng) {
          return [lat, lng];
        }
      }
    }

    // אם אין חיפוש או לא מצאנו מיקום, נחשב את המרכז של כל הארוחות
    const validDinners = dinners.filter(dinner => {
      let lat = dinner.address?.lat;
      let lng = dinner.address?.lng;
      if ((!lat || !lng) && Array.isArray(dinner.address?.coords) && dinner.address.coords.length === 2) {
        lat = dinner.address.coords[0];
        lng = dinner.address.coords[1];
      }
      return lat && lng;
    });

    if (validDinners.length === 0) {
      return defaultPosition;
    }

    // נחשב את המרכז הממוצע
    let totalLat = 0;
    let totalLng = 0;
    let count = 0;

    validDinners.forEach(dinner => {
      let lat = dinner.address?.lat;
      let lng = dinner.address?.lng;
      
      if ((!lat || !lng) && Array.isArray(dinner.address?.coords) && dinner.address.coords.length === 2) {
        lat = dinner.address.coords[0];
        lng = dinner.address.coords[1];
      }
      
      if (lat && lng) {
        totalLat += parseFloat(lat);
        totalLng += parseFloat(lng);
        count++;
      }
    });

    if (count === 0) {
      return defaultPosition;
    }

    return [totalLat / count, totalLng / count];
  };

  const mapCenter = calculateMapCenter();

  if (!isOpen) return null;

  return (
    <div className="map-modal-overlay" onClick={onClose}>
      <div className="map-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="map-modal-header">
          <h3>Map View</h3>
          <button className="map-modal-close" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="map-modal-body">
          <MapContainer
            center={mapCenter}
            zoom={searchLocation ? 10 : 6}
            style={{ height: '70vh', width: '100%' }}
            data-testid="dinners-map-modal"
          >
            <MapInvalidator />
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution="&copy; OpenStreetMap contributors"
            />
            {dinners.map((dinner) => {
              // בדיקה גם ל-lat/lng וגם ל-coords (מערך)
              let lat = dinner.address?.lat;
              let lng = dinner.address?.lng;
              if ((!lat || !lng) && Array.isArray(dinner.address?.coords) && dinner.address.coords.length === 2) {
                lat = dinner.address.coords[0];
                lng = dinner.address.coords[1];
              }
              if (lat && lng) {
                // סדר עדיפויות: קודם hostImageUrl, אחר כך hostProfileImg, ואז ברירת מחדל
                let imageUrl = dinner.hostImageUrl || dinner.hostProfileImg;
                if (!imageUrl || imageUrl === 'null' || imageUrl === 'undefined') imageUrl = defaultAvatar;
                return (
                  <Marker 
                    key={dinner.id} 
                    position={[lat, lng]} 
                    icon={createProfilePinIcon(imageUrl)}
                    eventHandlers={{
                      click: () => {
                        if (onDinnerClick) {
                          onDinnerClick(dinner);
                        }
                      }
                    }}
                  >
                    <Tooltip direction="top" offset={[0, -10]} opacity={1}>
                      <div style={{ textAlign: 'center', minWidth: '140px' }}>
                        <strong>{dinner.hostName}</strong>
                        <div style={{ marginTop: '4px', fontSize: '12px', color: '#666', fontWeight: 'bold' }}>
                          {dinner.address.neighborhood && `${dinner.address.neighborhood}, `}
                          {dinner.address.city}
                          {dinner.address.country && `, ${dinner.address.country}`}
                        </div>
                      </div>
                    </Tooltip>
                  </Marker>
                );
              }
              return null;
            })}
          </MapContainer>
        </div>
      </div>
    </div>
  );
}

export default MapModal; 